import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";

const statusColors = {
  pendente: "bg-amber-100 text-amber-700 border-amber-200",
  recebido: "bg-emerald-100 text-emerald-700 border-emerald-200",
  parcial: "bg-blue-100 text-blue-700 border-blue-200",
  atrasado: "bg-red-100 text-red-700 border-red-200",
  cancelado: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function Receivables() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ status: "", cliente_id: "", from_date: "", to_date: "" });
  const [openPay, setOpenPay] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [rec, setRec] = useState(null);
  const [payForm, setPayForm] = useState({ valor: 0, data: new Date().toISOString().slice(0, 10), forma_pagamento: "pix", observacoes: "" });
  const [editForm, setEditForm] = useState({ vencimento: "", valor: 0, forma_pagamento: "", observacoes: "" });

  const load = async () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.cliente_id) params.cliente_id = filters.cliente_id;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    const [r, c] = await Promise.all([api.get("/receivables", { params }), api.get("/clients")]);
    setItems(r.data); setClients(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const clearFilters = () => setFilters({ status: "", cliente_id: "", from_date: "", to_date: "" });
  const hasFilters = filters.status || filters.cliente_id || filters.from_date || filters.to_date;

  const cName = (id) => clients.find(c => c.id === id)?.nome || "—";

  const startPay = (r) => {
    setRec(r);
    setPayForm({ valor: r.valor - (r.valor_recebido || 0), data: new Date().toISOString().slice(0, 10), forma_pagamento: r.forma_pagamento || "pix", observacoes: "" });
    setOpenPay(true);
  };
  const submitPay = async () => {
    try {
      const { data } = await api.post("/payments", { receivable_id: rec.id, valor: parseFloat(payForm.valor), data: payForm.data, forma_pagamento: payForm.forma_pagamento, observacoes: payForm.observacoes });
      if (data.creditos_aplicados?.length) {
        const parts = data.creditos_aplicados.map(c => `#${c.parcela} (${fmtBRL(c.valor)})`).join(", ");
        toast.success(`Recebimento registrado. Crédito aplicado nas parcelas ${parts}`);
      } else {
        toast.success("Recebimento registrado");
      }
      if (data.credito_nao_aplicado) {
        toast.warning(`Não há mais parcelas pendentes nesta venda — ${fmtBRL(data.credito_nao_aplicado)} do valor recebido não foi alocado a nenhuma parcela.`);
      }
      setOpenPay(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startEdit = (r) => {
    setRec(r);
    setEditForm({ vencimento: r.vencimento, valor: r.valor, forma_pagamento: r.forma_pagamento || "pix", observacoes: r.observacoes || "" });
    setOpenEdit(true);
  };
  const submitEdit = async () => {
    try {
      await api.put(`/receivables/${rec.id}`, {
        vencimento: editForm.vencimento, valor: parseFloat(editForm.valor),
        forma_pagamento: editForm.forma_pagamento,
        observacoes: editForm.observacoes,
      });
      toast.success("Parcela atualizada"); setOpenEdit(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const totals = {
    previsto: items.reduce((a, r) => a + r.valor, 0),
    recebido: items.reduce((a, r) => a + (r.valor_recebido || 0), 0),
    vencido: items.filter(r => r.status === "atrasado").reduce((a, r) => a + (r.valor - (r.valor_recebido || 0)), 0),
  };

  const canReceive = can(user, "receive_payments");
  const canEdit = can(user, "edit_receivables");

  return (
    <Layout title="Contas a Receber" subtitle="Parcelas geradas pelas vendas">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Card className="p-4 rounded-sm"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Previsto</p><p className="kpi-num text-2xl mt-2">{fmtBRL(totals.previsto)}</p></Card>
        <Card className="p-4 rounded-sm"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Recebido</p><p className="kpi-num text-2xl mt-2 text-emerald-700">{fmtBRL(totals.recebido)}</p></Card>
        <Card className="p-4 rounded-sm"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Vencido</p><p className="kpi-num text-2xl mt-2 text-red-700">{fmtBRL(totals.vencido)}</p></Card>
      </div>
      <Card className="p-4 rounded-sm mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs font-semibold">Status</Label>
            <SearchableSelect
              value={filters.status}
              onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}
              options={[
                { value: "pendente", label: "Pendente" },
                { value: "atrasado", label: "Atrasado" },
                { value: "recebido", label: "Recebido" },
                { value: "parcial", label: "Parcial" },
                { value: "cancelado", label: "Cancelado" },
              ]}
              placeholder="Todos"
              searchPlaceholder="Buscar status..."
              allowClear
              testid="recv-filter-status"
              className="w-40 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Cliente</Label>
            <SearchableSelect
              value={filters.cliente_id}
              onValueChange={(v) => setFilters(f => ({ ...f, cliente_id: v }))}
              options={clients.map(c => ({
                value: c.id,
                label: c.nome,
                hint: c.cpf || c.cnpj || c.email || "",
              }))}
              placeholder="Todos os clientes"
              searchPlaceholder="Buscar por nome, CPF ou e-mail..."
              allowClear
              testid="recv-filter-cliente"
              className="w-64 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Vencimento de</Label>
            <Input data-testid="recv-filter-from" type="date" className="w-44 mt-1" value={filters.from_date} onChange={(e) => setFilters(f => ({ ...f, from_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs font-semibold">Vencimento até</Label>
            <Input data-testid="recv-filter-to" type="date" className="w-44 mt-1" value={filters.to_date} onChange={(e) => setFilters(f => ({ ...f, to_date: e.target.value }))} />
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="recv-filter-clear" className="mb-1">
              <X className="w-4 h-4 mr-1" />Limpar
            </Button>
          )}
        </div>
      </Card>
      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Cliente</TableHead><TableHead>Parcela</TableHead><TableHead>Vencimento</TableHead>
            <TableHead>Valor</TableHead><TableHead>Recebido</TableHead><TableHead>Forma</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>{items.map(r => (
            <TableRow key={r.id} data-testid={`recv-row-${r.id}`}>
              <TableCell>
                <div>{cName(r.cliente_id)}</div>
                {r.observacoes && <div className="text-[10px] text-muted-foreground truncate max-w-[260px]" title={r.observacoes}>📝 {r.observacoes}</div>}
              </TableCell>
              <TableCell>#{r.parcela}</TableCell>
              <TableCell>{fmtDate(r.vencimento)}</TableCell>
              <TableCell>{fmtBRL(r.valor)}</TableCell>
              <TableCell>{fmtBRL(r.valor_recebido)}</TableCell>
              <TableCell className="capitalize text-xs">{r.forma_pagamento || "—"}</TableCell>
              <TableCell><Badge variant="outline" className={`${statusColors[r.status]} capitalize text-[10px]`}>{r.status}</Badge></TableCell>
              <TableCell className="text-right space-x-1">
                {r.status !== "recebido" && r.status !== "cancelado" && canReceive && (
                  <Button data-testid={`recv-pay-${r.id}`} size="sm" variant="outline" onClick={() => startPay(r)}>Receber</Button>
                )}
                {canEdit && (
                  <Button data-testid={`recv-edit-${r.id}`} size="sm" variant="ghost" onClick={() => startEdit(r)}><Pencil className="w-4 h-4"/></Button>
                )}
              </TableCell>
            </TableRow>
          ))}{items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Sem parcelas</TableCell></TableRow>}</TableBody>
        </Table>
      </Card>

      <Dialog open={openPay} onOpenChange={setOpenPay}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar Recebimento</DialogTitle></DialogHeader>
          {rec && (<div className="space-y-3">
            <div className="text-sm text-muted-foreground">{cName(rec.cliente_id)} • Parcela #{rec.parcela} • Total {fmtBRL(rec.valor)} • Já recebido {fmtBRL(rec.valor_recebido)}</div>
            <p className="text-[10px] text-muted-foreground">Você pode registrar múltiplos recebimentos para uma mesma parcela, em datas e formas diferentes. Se o valor recebido for maior que o saldo desta parcela, o excedente é lançado automaticamente como crédito na próxima parcela da venda.</p>
            <div><Label className="text-xs font-semibold">Valor recebido</Label><Input data-testid="pay-valor" type="number" step="0.01" value={payForm.valor} onChange={(e) => setPayForm({...payForm, valor: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Data</Label><Input type="date" value={payForm.data} onChange={(e) => setPayForm({...payForm, data: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Forma de pagamento</Label>
              <Select value={payForm.forma_pagamento} onValueChange={(v) => setPayForm({...payForm, forma_pagamento: v})}>
                <SelectTrigger className="mt-1" data-testid="pay-forma"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="pix">PIX</SelectItem><SelectItem value="boleto">Boleto</SelectItem><SelectItem value="cartao">Cartão</SelectItem><SelectItem value="transferencia">Transferência</SelectItem><SelectItem value="dinheiro">Dinheiro</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Observações</Label><Input value={payForm.observacoes} onChange={(e) => setPayForm({...payForm, observacoes: e.target.value})} className="mt-1"/></div>
          </div>)}
          <DialogFooter><Button onClick={submitPay} data-testid="pay-confirm">Confirmar Recebimento</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Parcela</DialogTitle></DialogHeader>
          {rec && (<div className="space-y-3">
            <div className="text-sm text-muted-foreground">{cName(rec.cliente_id)} • Parcela #{rec.parcela}</div>
            <div><Label className="text-xs font-semibold">Vencimento</Label><Input data-testid="recv-edit-vencimento" type="date" value={editForm.vencimento} onChange={(e) => setEditForm({...editForm, vencimento: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Valor (R$)</Label><Input type="number" step="0.01" value={editForm.valor} onChange={(e) => setEditForm({...editForm, valor: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Forma padrão</Label>
              <Select value={editForm.forma_pagamento} onValueChange={(v) => setEditForm({...editForm, forma_pagamento: v})}>
                <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="pix">PIX</SelectItem><SelectItem value="boleto">Boleto</SelectItem><SelectItem value="cartao">Cartão</SelectItem><SelectItem value="transferencia">Transferência</SelectItem><SelectItem value="dinheiro">Dinheiro</SelectItem></SelectContent>
              </Select></div>
            <div>
              <Label className="text-xs font-semibold">Observações</Label>
              <Textarea
                data-testid="recv-edit-observacoes"
                rows={3}
                value={editForm.observacoes}
                onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })}
                placeholder="Ex.: cliente pediu para postergar; combinado desconto; aguardando confirmação…"
                className="mt-1"
              />
            </div>
          </div>)}
          <DialogFooter><Button onClick={submitEdit} data-testid="recv-edit-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
